import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Button,
  Spinner,
  Card,
  Row,
  Col,
  Dropdown,
} from "react-bootstrap";
import emailService from "../services/emailService";
import CampaignEmailComposer from "../utils/CampaignEmailComposer";
import { FaFileUpload, FaTrash } from "react-icons/fa";
import { companyID } from "../auth";
import toast from "react-hot-toast";
import Loader from "./Loader";

const CampaignMessageSender = () => {
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [selectedSendAs, setSelectedSendAs] = useState(null);
  const [emailConfig, setEmailConfig] = useState([]);
  const [errors, setErrors] = useState({
    file: "",
    subject: "",
    content: "",
  });
  const company_id = companyID();
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const editorRef = useRef(null);

  const handleSendAsChange = (value) => {
    setSelectedSendAs(value);
  };

  useEffect(() => {
    fetchTemplates();
    if (company_id) {
      fetchEmailConfig(company_id);
    }
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await emailService.getTemplates();
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const fetchEmailConfig = async (company_id) => {
    try {
      const response = await emailService.getEmailConfig(company_id);
      setEmailConfig(response.data);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  const handleTemplateChange = (templateId) => {
    const selected = templates.find((t) => t.id === templateId);
    setSelectedTemplate(templateId);
    setSubject(selected?.subject || "");
    setContent(selected?.email_content || "");
    if (selected?.html_content) {
      setErrors((prev) => ({ ...prev, content: "", subject: "" }));
    }
  };

  function getCompanyDetailsById(id) {
    const companyDetails = emailConfig.find((item) => item.id === id);
    return companyDetails
      ? {
          mail: companyDetails.email,
          password: companyDetails.password,
          companyName: companyDetails.company_name,
        }
      : {};
  }

  function resetData() {
    handleDelete();
    setFile(null);
    setSubject("");
    setContent("");
    setSelectedTemplate("");
    setDragActive(false);
    setSelectedSendAs(null);
    setErrors({ file: "", subject: "", content: "" });
  }

  const validateForm = () => {
    const newErrors = {};
    if (!file) newErrors.file = "Please upload an Excel file.";
    if (!subject.trim()) newErrors.subject = "Please enter email subject.";
    if (!content.trim())
      newErrors.content = "Please write or choose an email template.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    let sendAs = {};
    if (selectedSendAs) {
      sendAs = getCompanyDetailsById(selectedSendAs);
    }
    setLoading({ show: true, message: "Sending mail..." });
    const mailMessage = await editorRef.current.getEmailHtml();

    try {
      const response = await emailService.sendMailForCampaign(
        file,
        subject,
        mailMessage,
        sendAs?.mail,
        sendAs?.password,
        sendAs?.companyName
      );
      toast.success(`${response.message}, Campaign started successfully!`);
      resetData();
    } catch (error) {
      console.error("Failed to upload file", error.message);
      toast.error("Something went wrong, try again later.");
    }
    setLoading({ show: false, message: "" });
  };

  const handleDelete = () => {
    setFile(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = "";
    setErrors((prev) => ({ ...prev, file: "Please upload an Excel file" }));
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, subject: "" }));
    }
  };

  const handleContentChange = (newContent) => {
    setContent(newContent);
    if (newContent.trim()) {
      setErrors((prev) => ({ ...prev, content: "" }));
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Card className="shadow-sm border-0">
          <Card.Body>
            <h3 className="mb-4 text-primary">Email Campaign</h3>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <Col md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label className="fw-bold">
                      Excel File
                      <span
                        className="text-danger ms-1"
                        title="This field is mandatory."
                      >
                        *
                      </span>
                    </Form.Label>
                    <div
                      className={`border rounded p-4 text-center ${
                        dragActive
                          ? "border-primary bg-light"
                          : "border-secondary"
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      style={{ minHeight: "150px" }}
                    >
                      <FaFileUpload size={30} className="text-muted mb-2" />
                      <p className="mb-2">
                        {file ? file.name : "Drag & drop Excel file here"}
                      </p>
                      <div className="d-flex justify-content-center gap-2">
                        <Button variant="outline-primary" size="sm" as="label">
                          Browse File
                          <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                            accept=".xlsx,.xls"
                          />
                        </Button>
                        {file && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleDelete}
                            title="Delete selected file"
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                      {errors.file && (
                        <span className="text-danger mt-2">
                          * {errors.file}
                        </span>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col
                  md={6}
                  className="mb-2 d-flex flex-column justify-content-between gap-2"
                >
                  <Form.Group>
                    <Form.Label className="fw-bold">Template</Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-primary"
                        className="w-100 text-center"
                        style={{ height: "38px" }}
                      >
                        {selectedTemplate
                          ? templates.find((t) => t.id === selectedTemplate)
                              ?.template_name
                          : "Select a template"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        <Dropdown.Item onClick={() => handleTemplateChange("")}>
                          Create New Template
                        </Dropdown.Item>
                        {templates &&
                          templates.map((template) => (
                            <Dropdown.Item
                              key={template.id}
                              onClick={() => handleTemplateChange(template.id)}
                            >
                              {template.template_name}
                            </Dropdown.Item>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-bold">Send Mail As</Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-primary"
                        className="w-100 text-center"
                        style={{ height: "38px" }}
                      >
                        {selectedSendAs
                          ? emailConfig.find((t) => t.id === selectedSendAs)
                              ?.company_name
                          : "Select a sender"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        {emailConfig.map((email) => (
                          <Dropdown.Item
                            key={email.id}
                            onClick={() => handleSendAsChange(email.id)}
                          >
                            {email.company_name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  Email Subject
                  <span
                    className="text-danger ms-1"
                    title="This field is mandatory."
                  >
                    *
                  </span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter email subject"
                  value={subject}
                  onChange={handleSubjectChange}
                />
                {errors.subject && (
                  <span className="text-danger mt-2">* {errors.subject}</span>
                )}
              </Form.Group>

              {selectedTemplate ? (
                <Row className="mb-4">
                  <Col>
                    <Form.Group>
                      <Form.Label className="fw-bold">
                        Dynamic Fields of Template
                        <span
                          className="text-danger ms-1"
                          title="This field is mandatory."
                        >
                          *
                        </span>
                      </Form.Label>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {(templates &&
                          templates
                            .find((t) => t.id === selectedTemplate)
                            ?.dynamic_keywords?.map((keyword, index) => (
                              <span
                                key={index}
                                className="badge bg-primary-subtle text-primary p-2"
                              >
                                {keyword}
                              </span>
                            ))) || (
                          <span className="text-muted">
                            No dynamic fields found
                          </span>
                        )}
                      </div>
                      <Form.Text className="text-muted fst-italic">
                        Note: These fields are mandatory and must be included in
                        your uploaded Excel file in the same sequence.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              ) : (
                <p className="text-muted fst-italic">
                  Note: Dynamic fields {"${}"} must be included in your uploaded
                  Excel file in the same sequence.
                </p>
              )}

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  Email Content
                  <span
                    className="text-danger ms-1"
                    title="This field is mandatory."
                  >
                    *
                  </span>
                </Form.Label>
                <CampaignEmailComposer
                  ref={editorRef}
                  content={content}
                  setContent={handleContentChange}
                  template={selectedTemplate}
                />
                {errors.content && (
                  <span className="text-danger mt-2">* {errors.content}</span>
                )}
              </Form.Group>

              <div className="text-end">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading.show}
                  className="px-4 py-2"
                >
                  {loading.show ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : null}
                  {loading.show ? "Processing..." : "Send Emails"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
      {loading.show && (
        <Loader show={loading.show} loadingMessage={loading.message} />
      )}
    </div>
  );
};

export default CampaignMessageSender;
